import { ArchiveIcon, Pencil1Icon, ResetIcon } from "@radix-ui/react-icons";
import { ColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";

import { IMeasurement } from "../../../@types/admin/AdminMeasurement";
import { TableSorted } from "../../../components/TableSorted";
import { useBillableDeviceTypes } from "../../../hooks/queries/admin/billableDevices";
import {
  MEASUREMENTS_QUERY_KEY,
  useMeasurementTypeValidations,
  useMeasurements,
} from "../../../hooks/queries/admin/measurements";
import { useModal } from "../../../hooks/useModal";
import { getLongFormattedDate } from "../../../utils/dateFormatter";
import {
  AdminSelectField,
  EmailLink,
  ErrorMessage,
  FilterButton,
  FilterGroup,
  FilterInput,
  FilterLabel,
  FiltersContainer,
  PageContainer,
  PageHeader,
  PageTitle,
  RowAction,
  StatusBadge,
  TableContainer,
  TableLoadingState,
  TablePagination,
  TableWrapper,
} from "../shared";
import { RowActions, TypeTag, TypeTags } from "./AdminPatients.styles";
import { ArchiveMeasurementModal } from "./ArchiveMeasurementModal";
import { EditMeasurementModal } from "./EditMeasurementModal";

const DEFAULT_PAGE_SIZE = 10;

interface IFilterState {
  patientName: string;
  hospitalName: string;
  wardName: string;
  measurementType: string;
  source: string;
}

const EMPTY_FILTERS: IFilterState = {
  patientName: "",
  hospitalName: "",
  wardName: "",
  measurementType: "",
  source: "",
};

/**
 * Measurements admin page (EN-5158), migrated from the Retool MeasurementList
 * page plus `moduleMeasurementList`. Filters the measurement list across all
 * patients, edits a measurement with the ported client-side validation, and
 * archives or unarchives one.
 *
 * ⚠️ This page writes to the clinical record. The validation ported from
 * `validateMeasurements.js` (see measurementEdit.ts) is what stops a bad
 * value being saved — do not route a save around it.
 *
 * Server-paginated, unlike the other migrated pages: the list spans every
 * patient, so there is no catalogue-sized set to hold client-side. The query
 * stays idle until Find is pressed, as Retool's did — an unfiltered first
 * page across all patients is a slow read (Retool gave it a 60s timeout).
 *
 * The leftover eMED integration queries in `page_MeasurementList` are ignored;
 * the export was cloned from the eMED page and its components never
 * referenced them.
 */
export function AdminMeasurements() {
  document.title = "Measurements - Admin";

  const { openModal, closeModal } = useModal();
  const {
    measurementTypes,
    isLoading: isCatalogLoading,
    isError: isCatalogError,
  } = useMeasurementTypeValidations();
  const { deviceTypes } = useBillableDeviceTypes();

  const [draft, setDraft] = useState<IFilterState>(EMPTY_FILTERS);
  // The filters the list is actually reading. Separate from the draft so
  // typing a patient name doesn't fire a query per keystroke — Retool used
  // its Find button for the same reason.
  const [applied, setApplied] = useState<IFilterState | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const { measurements, totalCount, isFetching, isError } = useMeasurements(
    { page, pageSize, ...(applied ?? EMPTY_FILTERS) },
    applied !== null
  );

  const setFilter = (key: keyof IFilterState, value: string) =>
    setDraft((previous) => ({ ...previous, [key]: value }));

  const handleFind = () => {
    setPage(1);
    setApplied(draft);
  };

  const handleClear = () => {
    setDraft(EMPTY_FILTERS);
    setPage(1);
    setApplied(null);
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const pagination = {
    currentPage: page,
    pageSize,
    totalPages,
    totalItems: totalCount,
    hasNextPage: page < totalPages,
    hasPreviousPage: page > 1,
  };

  const handleEdit = (measurement: IMeasurement) => {
    // The catalogue carries the min/max the edit form validates against.
    // Without it every value would pass validation, so editing stays off
    // until it has loaded (the banner below covers the error case).
    if (isCatalogLoading || measurementTypes.length === 0) return;
    openModal(
      <EditMeasurementModal
        measurement={measurement}
        measurementTypes={measurementTypes}
        onClose={closeModal}
      />,
      { width: "min(900px, 94vw)", maxHeight: "90vh", showCloseButton: true }
    );
  };

  const handleArchive = (measurement: IMeasurement) => {
    openModal(
      <ArchiveMeasurementModal
        measurement={measurement}
        measurementTypes={measurementTypes}
        onClose={closeModal}
      />,
      { width: "520px", maxHeight: "90vh", showCloseButton: true }
    );
  };

  const columns: ColumnDef<IMeasurement, string>[] = useMemo(
    () => [
      {
        accessorKey: "id",
        header: "Measurement ID",
        cell: ({ row }) => <span>{row.original.id}</span>,
        enableSorting: false,
      },
      {
        accessorKey: "patientName",
        header: "Patient name",
        enableSorting: false,
      },
      {
        accessorKey: "patientId",
        header: "Patient ID",
        cell: ({ row }) => <span>{row.original.patientId}</span>,
        enableSorting: false,
      },
      {
        accessorKey: "email",
        header: "Email",
        cell: ({ row }) => (
          <EmailLink
            href={`mailto:${row.original.email}`}
            onClick={(e) => e.stopPropagation()}
          >
            {row.original.email}
          </EmailLink>
        ),
        enableSorting: false,
      },
      { accessorKey: "source", header: "Source", enableSorting: false },
      {
        accessorKey: "time",
        header: "Time",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.time, "-")}</span>
        ),
        enableSorting: false,
      },
      {
        id: "measurementTypes",
        header: "Measurement types",
        accessorFn: (measurement) =>
          (measurement.measurementTypes ?? []).join(", "),
        cell: ({ row }) => (
          <TypeTags>
            {(row.original.measurementTypes ?? []).map((type) => (
              <TypeTag key={type}>{type}</TypeTag>
            ))}
          </TypeTags>
        ),
        enableSorting: false,
      },
      {
        // `isArchived` is false for most rows, and TableSorted dashes a falsy
        // value — the accessor renders the word so "Active" is explicit.
        // Headed "Status" rather than Retool's "Archived": the cell says
        // Active or Archived, and a column headed with one of its own values
        // reads as a filter.
        id: "isArchived",
        header: "Status",
        accessorFn: (measurement) =>
          measurement.isArchived ? "Archived" : "Active",
        cell: ({ row }) => (
          <StatusBadge status={row.original.isArchived ? "inactive" : "active"}>
            {row.original.isArchived ? "Archived" : "Active"}
          </StatusBadge>
        ),
        enableSorting: false,
      },
      { accessorKey: "hospitalName", header: "Hospital", enableSorting: false },
      { accessorKey: "wardName", header: "Ward", enableSorting: false },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <RowActions>
            <RowAction
              label="Edit"
              icon={<Pencil1Icon />}
              onClick={() => {
                handleEdit(row.original);
              }}
            />
            <RowAction
              label={row.original.isArchived ? "Unarchive" : "Archive"}
              icon={row.original.isArchived ? <ResetIcon /> : <ArchiveIcon />}
              danger={!row.original.isArchived}
              onClick={() => {
                handleArchive(row.original);
              }}
            />
          </RowActions>
        ),
      },
    ],
    [measurementTypes]
  );

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Measurements</PageTitle>
      </PageHeader>

      <FiltersContainer>
        <FilterGroup>
          <FilterLabel htmlFor="measurements-patient">Patient name</FilterLabel>
          <FilterInput
            id="measurements-patient"
            type="text"
            value={draft.patientName}
            onChange={(e) => setFilter("patientName", e.target.value)}
          />
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="measurements-hospital">Hospital</FilterLabel>
          <FilterInput
            id="measurements-hospital"
            type="text"
            value={draft.hospitalName}
            onChange={(e) => setFilter("hospitalName", e.target.value)}
          />
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="measurements-ward">Ward</FilterLabel>
          <FilterInput
            id="measurements-ward"
            type="text"
            value={draft.wardName}
            onChange={(e) => setFilter("wardName", e.target.value)}
          />
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="measurements-type">
            Measurement type
          </FilterLabel>
          <AdminSelectField
            id="measurements-type"
            value={draft.measurementType}
            onChange={(e) => setFilter("measurementType", e.target.value)}
          >
            <option value="">All</option>
            {measurementTypes.map((type) => (
              <option key={type.measurementType} value={type.measurementType}>
                {type.displayName}
              </option>
            ))}
          </AdminSelectField>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="measurements-source">Source</FilterLabel>
          <AdminSelectField
            id="measurements-source"
            value={draft.source}
            onChange={(e) => setFilter("source", e.target.value)}
          >
            <option value="">All</option>
            {deviceTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </AdminSelectField>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>&nbsp;</FilterLabel>
          <RowActions>
            <FilterButton
              variant="secondary"
              disabled={applied === null}
              onClick={handleClear}
            >
              Clear
            </FilterButton>
            <FilterButton variant="primary" onClick={handleFind}>
              Find
            </FilterButton>
          </RowActions>
        </FilterGroup>
      </FiltersContainer>

      {isError && (
        <ErrorMessage>
          Failed to load the measurements. Narrow the filters and try again.
        </ErrorMessage>
      )}

      {isCatalogError && (
        <ErrorMessage>
          Failed to load the measurement types catalogue, so values cannot be
          validated and editing is disabled. Reload the page to try again.
        </ErrorMessage>
      )}

      <TableContainer>
        <TableWrapper refreshKeys={[[MEASUREMENTS_QUERY_KEY]]}>
          <TableSorted
            columns={columns}
            data={measurements}
            emptyStateText={(() => {
              if (applied === null) {
                return "Set the filters you need and press Find";
              }
              return isFetching ? <TableLoadingState /> : "No rows found";
            })()}
          />
        </TableWrapper>
        {applied !== null && (
          <TablePagination
            pagination={pagination}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            // Server-paginated: unlike the client-side lists, this can only
            // write the page that has been fetched. The button's row count
            // says so.
            exportCsv={{
              filename: "measurements",
              columns,
              rows: measurements,
              currentPageOnly: true,
            }}
          />
        )}
      </TableContainer>
    </PageContainer>
  );
}
